import React from 'react';
import type { FormField } from '../../types/form';
import { isDisplayOnlyFieldType } from '../../types/form';
import {
  alignmentClass,
  buildYoutubeEmbedUrl,
  extractYoutubeVideoId,
  formatMaskedInput,
  resolveDisplayImageUrl,
  resolveImageBlockSettings,
  resolveInlineImageSettings,
  resolveMaskSettings,
  resolveRatingSettings,
  resolveVideoBlockSettings,
} from '../../types/formFieldSettings';
import { FormRatingScale } from './FormRatingScale';

interface FormFieldRendererProps {
  field: FormField;
  value: string;
  error?: string;
  disabled?: boolean;
  onChange: (value: string) => void;
  onFileChange?: (file: File | null) => void;
}

export const FormFieldRenderer: React.FC<FormFieldRendererProps> = ({
  field,
  value,
  error,
  disabled = false,
  onChange,
  onFileChange,
}) => {
  const inputClass =
    'w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:opacity-60';

  const handleCheckboxChange = (optionLabel: string, checked: boolean) => {
    const selected = value
      ? value.split('\n').filter((item) => item.length > 0)
      : [];

    let next: string[];
    if (checked) {
      next = selected.includes(optionLabel)
        ? selected
        : [...selected, optionLabel];
    } else {
      next = selected.filter((item) => item !== optionLabel);
    }

    onChange(next.join('\n'));
  };

  const selectedCheckboxes = value
    ? value.split('\n').filter((item) => item.length > 0)
    : [];

  const ratingSettings =
    field.type === 'Rating' ? resolveRatingSettings(field) : null;
  const ratingValue = Number.parseInt(value, 10);

  const inlineImage = !isDisplayOnlyFieldType(field.type)
    ? resolveInlineImageSettings(field)
    : null;
  const inlineImageUrl = inlineImage
    ? resolveDisplayImageUrl({
        inlineImageUrl: inlineImage.inlineImageUrl,
        imageSource: inlineImage.imageSource,
        externalUrl: inlineImage.externalUrl,
      })
    : undefined;

  if (field.type === 'Image')
  {
    const imageSettings = resolveImageBlockSettings(field);
    const imageUrl = resolveDisplayImageUrl({
      mediaUrl: imageSettings.mediaUrl,
      imageSource: imageSettings.imageSource,
      externalUrl: imageSettings.externalUrl,
    });

    return (
      <div>
        {field.title && (
          <p className="mb-2 text-sm font-medium text-gray-900">{field.title}</p>
        )}
        {imageUrl ? (
          <div className={`flex ${alignmentClass(imageSettings.alignment)}`}>
            <img
              src={imageUrl}
              alt={field.title || 'Imagem'}
              className="max-h-96 max-w-full rounded-md object-contain"
            />
          </div>
        ) : null}
      </div>
    );
  }

  if (field.type === 'Video')
  {
    const videoSettings = resolveVideoBlockSettings(field);
    const videoId = videoSettings.youtubeUrl
      ? extractYoutubeVideoId(videoSettings.youtubeUrl)
      : null;

    return (
      <div>
        {field.title && (
          <p className="mb-2 text-sm font-medium text-gray-900">{field.title}</p>
        )}
        {videoId ? (
          <div className="overflow-hidden rounded-md border border-gray-200 bg-black">
            <iframe
              title={field.title || 'Vídeo'}
              src={buildYoutubeEmbedUrl(videoId)}
              className="aspect-video w-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div>
      <label className="mb-1.5 flex flex-wrap items-center gap-2 text-sm font-medium text-gray-900">
        <span>{field.title}</span>
        {field.isRequired && (
          <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[11px] font-medium uppercase tracking-wide text-gray-600">
            obrigatório
          </span>
        )}
      </label>
      {field.description && (
        <p className="mb-3 text-sm text-gray-500">{field.description}</p>
      )}

      {inlineImageUrl && (
        <div className={`mb-3 flex ${alignmentClass(inlineImage?.alignment)}`}>
          <img
            src={inlineImageUrl}
            alt=""
            className="max-h-64 max-w-full rounded-md object-contain"
          />
        </div>
      )}

      {field.type === 'ShortText' && (() => {
        const mask = resolveMaskSettings(field);
        return (
          <input
            type="text"
            value={value}
            disabled={disabled}
            onChange={(e) =>
              onChange(
                mask.kind && mask.kind !== 'None'
                  ? formatMaskedInput(e.target.value, mask)
                  : e.target.value
              )
            }
            className={inputClass}
            placeholder="Sua resposta"
            inputMode={
              mask.kind === 'Phone'
              || mask.kind === 'Cpf'
              || mask.kind === 'Cep'
              || mask.kind === 'Currency'
                ? 'numeric'
                : undefined
            }
          />
        );
      })()}

      {field.type === 'Paragraph' && (
        <textarea
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          className={`${inputClass} min-h-[96px] resize-y`}
          placeholder="Sua resposta"
          rows={4}
        />
      )}

      {field.type === 'MultipleChoice' && (
        <div className="space-y-2">
          {field.options
            .slice()
            .sort((a, b) => a.order - b.order)
            .map((option) => (
              <label
                key={option.id}
                className="flex cursor-pointer items-center gap-3 text-gray-800"
              >
                <input
                  type="radio"
                  name={field.id}
                  value={option.label}
                  checked={value === option.label}
                  disabled={disabled}
                  onChange={() => onChange(option.label)}
                  className="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span>{option.label}</span>
              </label>
            ))}
        </div>
      )}

      {field.type === 'Checkboxes' && (
        <div className="space-y-2">
          {field.options
            .slice()
            .sort((a, b) => a.order - b.order)
            .map((option) => (
              <label
                key={option.id}
                className="flex cursor-pointer items-center gap-3 text-gray-800"
              >
                <input
                  type="checkbox"
                  checked={selectedCheckboxes.includes(option.label)}
                  disabled={disabled}
                  onChange={(e) =>
                    handleCheckboxChange(option.label, e.target.checked)
                  }
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span>{option.label}</span>
              </label>
            ))}
        </div>
      )}

      {field.type === 'Dropdown' && (
        <select
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          className="w-full max-w-xs rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-900 focus:border-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-500"
        >
          <option value="">Selecione</option>
          {field.options
            .slice()
            .sort((a, b) => a.order - b.order)
            .map((option) => (
              <option key={option.id} value={option.label}>
                {option.label}
              </option>
            ))}
        </select>
      )}

      {field.type === 'Date' && (
        <input
          type="date"
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          className="picker-native-input rounded-md border border-gray-300 px-3 py-2 focus:border-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
      )}

      {field.type === 'Time' && (
        <input
          type="time"
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          className="picker-native-input rounded-md border border-gray-300 px-3 py-2 focus:border-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
      )}

      {field.type === 'FileUpload' && (
        <div>
          <input
            type="file"
            disabled={disabled}
            onChange={(e) => {
              const file = e.target.files?.[0] ?? null;
              onChange(file?.name ?? '');
              if (onFileChange) {
                onFileChange(file);
              }
            }}
            className="block w-full text-sm text-gray-600 file:mr-4 file:rounded-md file:border-0 file:bg-blue-50 file:px-4 file:py-2 file:text-sm file:font-medium file:text-blue-700 hover:file:bg-blue-100"
          />
          {value && (
            <p className="mt-2 text-xs text-gray-500">Arquivo selecionado: {value}</p>
          )}
        </div>
      )}

      {field.type === 'Rating' && ratingSettings && (
        <FormRatingScale
          scale={ratingSettings.scale}
          icon={ratingSettings.icon}
          value={Number.isFinite(ratingValue) ? ratingValue : 0}
          disabled={disabled}
          interactive
          onChange={(nextValue) => onChange(String(nextValue))}
        />
      )}

      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
    </div>
  );
};
